/**
 * Converts time string from 12-hour format (e.g., "9:00am", "5:00pm") to 24-hour format (e.g., "09:00", "17:00")
 * Also handles already-formatted 24-hour times
 */
export const convertTo24Hour = (timeStr: string): string => {
  const trimmed = timeStr.trim().toLowerCase();

  // If already in HH:mm format (no am/pm), return as-is with padding
  if (!trimmed.includes('am') && !trimmed.includes('pm')) {
    const [hours, minutes] = trimmed.split(':');
    if (hours && minutes) {
      return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
    }
    return trimmed;
  }

  // Parse 12-hour format
  const isPM = trimmed.includes('pm');
  const timeWithoutPeriod = trimmed.replace(/am|pm/g, '').trim();
  const [hoursStr, minutesStr] = timeWithoutPeriod.split(':');

  let hours = parseInt(hoursStr, 10);
  const minutes = minutesStr || '00';

  // Convert to 24-hour format
  if (isPM && hours !== 12) {
    hours += 12;
  } else if (!isPM && hours === 12) {
    hours = 0;
  }

  return `${hours.toString().padStart(2, '0')}:${minutes.padStart(2, '0')}`;
};

/**
 * Converts time string from 24-hour format to 12-hour format for display
 */
export const convertTo12Hour = (timeStr: string): string => {
  const [hoursStr, minutesStr] = timeStr.split(':');
  let hours = parseInt(hoursStr, 10);
  const minutes = minutesStr || '00';

  const period = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;

  return `${hours}:${minutes}${period}`;
};

/**
 * Validates if a time string can be parsed correctly
 * Returns an error message if invalid, or null if valid
 */
export const validateTimeFormat = (timeStr: string): string | null => {
  if (!timeStr || timeStr.trim() === '') {
    return null; // Empty is okay
  }

  const trimmed = timeStr.trim().toLowerCase();

  // Check for basic time format patterns
  const has12HourFormat = trimmed.includes('am') || trimmed.includes('pm');
  const timePattern = /^(\d{1,2}):(\d{2})\s*(am|pm)?$/;
  const match = trimmed.match(timePattern);

  if (!match) {
    return 'Invalid format. Use 9:00am or 09:00';
  }

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);

  // Validate hours
  if (has12HourFormat) {
    if (hours < 1 || hours > 12) {
      return 'Hours must be between 1-12 for 12-hour format';
    }
  } else {
    if (hours < 0 || hours > 23) {
      return 'Hours must be between 0-23 for 24-hour format';
    }
  }

  // Validate minutes
  if (minutes < 0 || minutes > 59) {
    return 'Minutes must be between 0-59';
  }

  return null;
};

/**
 * Checks if two time intervals overlap
 * Times should be in HH:mm format
 */
const timeToMinutes = (timeStr: string): number => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

interface TimeInterval {
  from: string;
  to: string;
}

/**
 * Validates that a single interval has a valid start and end time
 * Returns an error message if the interval is invalid (start >= end), or null if valid
 */
const validateIntervalOrder = (interval: { from: string; to: string }): string | null => {
  const fromMinutes = timeToMinutes(interval.from);
  const toMinutes = timeToMinutes(interval.to);

  if (fromMinutes >= toMinutes) {
    const fromFormatted = convertTo12Hour(interval.from);
    const toFormatted = convertTo12Hour(interval.to);
    return `Start time (${fromFormatted}) must be before end time (${toFormatted})`;
  }

  return null;
};

export const validateNoOverlappingIntervals = (intervals: TimeInterval[]): string | null => {
  // Convert to 24-hour format for consistent comparison
  const normalizedIntervals = intervals.map((interval) => ({
    from: convertTo24Hour(interval.from),
    to: convertTo24Hour(interval.to)
  }));

  // Validate that each interval has 'from' before 'to'
  for (let i = 0; i < normalizedIntervals.length; i++) {
    const error = validateIntervalOrder(normalizedIntervals[i]);
    if (error) {
      return error;
    }
  }

  // Early return if 0 or 1 interval (no overlaps possible)
  if (intervals.length <= 1) {
    return null;
  }

  // Check each pair for overlap
  for (let i = 0; i < normalizedIntervals.length; i++) {
    for (let j = i + 1; j < normalizedIntervals.length; j++) {
      const interval1 = normalizedIntervals[i];
      const interval2 = normalizedIntervals[j];

      const from1 = timeToMinutes(interval1.from);
      const to1 = timeToMinutes(interval1.to);
      const from2 = timeToMinutes(interval2.from);
      const to2 = timeToMinutes(interval2.to);

      // Check if intervals overlap
      // They overlap if: from1 < to2 AND from2 < to1
      if (from1 < to2 && from2 < to1) {
        const from1Formatted = convertTo12Hour(interval1.from);
        const to1Formatted = convertTo12Hour(interval1.to);
        const from2Formatted = convertTo12Hour(interval2.from);
        const to2Formatted = convertTo12Hour(interval2.to);
        return `Intervals overlap: ${from1Formatted}-${to1Formatted} and ${from2Formatted}-${to2Formatted}`;
      }
    }
  }

  return null;
};
